import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import s from "./Testing.module.css";
import { useCreateTestRecord, useReviseTestRecord, useUploadTestFile } from "../../api/queries";
import { parseResults, type NewTestInput, type TestRecordDetail, type TestType } from "../../domain/testing";
import { readResultsFile } from "./readResultsFile";
import { isoDay } from "../../domain/workOrderReport";
import { Button, Field, Input, Select } from "../../design/components";
import { t } from "../../i18n";

/**
 * Record a test as it was carried out.
 *
 * What the form asks for follows the test type: results pasted from the
 * contractor's sheet for a test judged per asset, the summary figures for a
 * ratio test, a verdict and its basis for one an engineer judges. The platform
 * judges it on arrival and keeps it as recorded — there is no edit afterwards,
 * so the form shows what it read before sending it.
 *
 * Opened with `revise`, it is the same form filled with the record as it
 * stands, and what it sends is the record's next revision, with the reason
 * — the revision it replaces is kept and marked superseded.
 */
export function RecordTestDialog({
  types,
  onClose,
  onCreated,
  initialType,
  revise,
}: {
  types: TestType[];
  onClose: () => void;
  onCreated: (id: string) => void;
  initialType?: string;
  /** Fill the form with this record and send the result as its next revision. */
  revise?: TestRecordDetail;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const create = useCreateTestRecord();
  const reviseMutation = useReviseTestRecord(revise?.record.id ?? "");
  const saving = revise ? reviseMutation : create;
  const rec = revise?.record;
  const [key, setKey] = useState(rec?.testType ?? initialType ?? types[0]?.key ?? "");
  const type = types.find((ty) => ty.key === key);
  const [f, setF] = useState({
    performedOn: rec?.performedOn ?? isoDay(new Date()),
    title: rec?.title ?? "",
    performedBy: rec?.performedBy ?? "",
    device: rec?.device ?? "",
    method: rec?.method ?? "",
    scope: rec?.scope ?? "",
    notes: rec?.notes ?? "",
    nextDue: rec?.nextDue ?? "",
  });
  const [summary, setSummary] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(rec?.summary ?? {}).map(([k, v]) => [k, String(v ?? "")])),
  );
  const [resultsText, setResultsText] = useState(
    (revise?.results ?? [])
      .map((r) => [r.assetTag ?? r.location, r.value, r.specified].filter((x) => x != null).join(", "))
      .join("\n"),
  );
  const [outcome, setOutcome] = useState<"pass" | "attention" | "fail">(
    rec?.judged && rec.outcome !== "not_assessed" ? rec.outcome : "pass",
  );
  const [basis, setBasis] = useState(rec?.judged ? rec.outcomeBasis : "");
  const [reason, setReason] = useState("");
  const [sourceFile, setSourceFile] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const upload = useUploadTestFile(createdId ?? "");
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup — see CreateWorkOrderDialog.
  }, []);

  const parsed = useMemo(() => parseResults(resultsText), [resultsText]);
  const pctBased = type?.thresholds.some((th) => th.basis === "pct_of_specified");
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  const ready =
    !!type &&
    !!f.performedOn &&
    (type.mode !== "threshold" || (parsed.rows.length > 0 && parsed.errors.length === 0)) &&
    (type.mode !== "judged" || basis.trim().length > 0) &&
    (!revise || reason.trim().length > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!type || !ready) return;
    const input: NewTestInput = {
      testType: type.key,
      performedOn: f.performedOn,
      title: f.title.trim() || undefined,
      performedBy: f.performedBy.trim() || undefined,
      device: f.device.trim() || undefined,
      method: f.method.trim() || undefined,
      scope: f.scope.trim() || undefined,
      notes: f.notes.trim() || undefined,
      nextDue: f.nextDue || undefined,
      summary: Object.fromEntries(
        type.summaryFields
          .filter((sf) => summary[sf.key]?.trim())
          .map((sf) => [sf.key, sf.type === "number" ? Number(summary[sf.key]) : summary[sf.key].trim()]),
      ),
      results: type.mode === "threshold" ? parsed.rows : [],
      ...(type.mode === "judged" ? { outcome, outcomeBasis: basis.trim() } : {}),
      ...(sourceFile && type.mode === "threshold"
        ? { source: "file" as const, sourceFile }
        : { source: "form" as const }),
    };
    const done = (r: { id: string }) => {
      setCreatedId(r.id);
      if (files.length === 0) onCreated(r.id);
    };
    if (revise) reviseMutation.mutate({ ...input, reason: reason.trim() }, { onSuccess: done });
    else create.mutate(input, { onSuccess: done });
  };

  const importFile = async (file: File | undefined) => {
    if (!file) return;
    setImportError(null);
    try {
      const text = await readResultsFile(file);
      setResultsText(text);
      setSourceFile(file.name);
      // The file the results came from is kept with the record.
      setFiles((xs) => (xs.some((x) => x.name === file.name) ? xs : [...xs, file]));
    } catch (e) {
      setImportError(`${file.name}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Files go up once the record exists, one at a time, so a failure names the file.
  useEffect(() => {
    if (!createdId || files.length === 0) return;
    let live = true;
    (async () => {
      for (const file of files) {
        try {
          await upload.mutateAsync({ file, note: file.name });
        } catch (e) {
          if (live) setUploadError(`${file.name}: ${e instanceof Error ? e.message : String(e)}`);
          return;
        }
      }
      if (live) onCreated(createdId);
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdId]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      style={{
        background: "var(--panel)",
        color: "var(--ink)",
        border: "1px solid var(--line)",
        borderRadius: "var(--radius)",
        padding: 20,
        width: "min(720px, 96vw)",
      }}
      aria-label={revise ? t("Update test results") : t("Record a test")}
    >
      <form className={s.form} onSubmit={submit}>
        <h2 className={s.wide}>
          {revise
            ? t("Update {reference} — revision {n}", { reference: rec!.reference, n: rec!.revision + 1 })
            : t("Record a test")}
        </h2>
        {revise && (
          <div className={`${s.wide} ${s.note}`}>
            {t(
              "The record is kept as revision {n} and marked superseded; this becomes the current record and is judged again. Change what needs correcting and say why.",
              { n: rec!.revision },
            )}
          </div>
        )}
        {revise && (
          <div className={s.wide}>
            <Field label="Reason for the update (required, recorded)">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("e.g. Contractor re-issued the report; two lights were keyed against the wrong circuit")}
                required
                autoFocus
              />
            </Field>
          </div>
        )}
        <div className={s.wide}>
          <Field label="Test">
            <Select value={key} onChange={(e) => setKey(e.target.value)} disabled={!!revise}>
              {types.map((ty) => (
                <option key={ty.key} value={ty.key}>
                  {ty.name}
                </option>
              ))}
            </Select>
          </Field>
          {type && <div className={s.frameworks}>{type.frameworks.join(" · ")}</div>}
        </div>
        <Field label="Performed on">
          <Input
            type="date"
            value={f.performedOn}
            max={isoDay(new Date())}
            onChange={(e) => set("performedOn")(e.target.value)}
            required
          />
        </Field>
        <Field label="Performed by">
          <Input
            value={f.performedBy}
            onChange={(e) => set("performedBy")(e.target.value)}
            placeholder={t("Contractor or team")}
          />
        </Field>
        <Field label="Device / equipment">
          <Input value={f.device} onChange={(e) => set("device")(e.target.value)} />
        </Field>
        <Field label="Method / standard followed">
          <Input value={f.method} onChange={(e) => set("method")(e.target.value)} />
        </Field>
        <div className={s.wide}>
          <Field label="Title (optional)">
            <Input value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder={type?.name} />
          </Field>
        </div>
        <div className={s.wide}>
          <Field label="Scope">
            <Input
              value={f.scope}
              onChange={(e) => set("scope")(e.target.value)}
              placeholder={t("e.g. RWY 17/35 edge and centerline lights")}
            />
          </Field>
        </div>

        {type?.summaryFields.map((sf) => (
          <Field key={sf.key} label={sf.label}>
            <Input
              type={sf.type === "number" ? "number" : "text"}
              step="any"
              value={summary[sf.key] ?? ""}
              onChange={(e) => setSummary((x) => ({ ...x, [sf.key]: e.target.value }))}
            />
          </Field>
        ))}

        {type?.mode === "threshold" && (
          <div className={s.wide}>
            <div className={s.filters} style={{ marginBottom: 6 }}>
              <label className={s.note} style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <b>{t("Upload results file")}</b>
                <input
                  type="file"
                  accept=".csv,.xlsx,text/csv"
                  onChange={(e) => void importFile(e.target.files?.[0])}
                  aria-label={t("Upload results file")}
                />
              </label>
              {sourceFile && (
                <span className={`${s.badge} ${s.demo}`} title={t("Recorded as the data source of this record")}>
                  {t("FROM {file}", { file: sourceFile })}
                </span>
              )}
            </div>
            {importError && <div className={s.fail}>{importError}</div>}
            <Field
              label={
                pctBased
                  ? t("Results — one per line: asset tag or location, value, specified value ({unit})", {
                      unit: type.unit ?? "",
                    })
                  : t("Results — one per line: asset tag or location, value ({unit})", { unit: type.unit ?? "" })
              }
            >
              <textarea
                className={s.textarea}
                rows={6}
                value={resultsText}
                onChange={(e) => {
                  setResultsText(e.target.value);
                  if (sourceFile) setSourceFile(null); // edited by hand: no longer just the file
                }}
                placeholder={
                  pctBased
                    ? "AGL-EDGE-0001, 7800, 10000\nAGL-EDGE-0002, 4100, 10000"
                    : "MRK-CL-001, 240\nMRK-CL-002, 132"
                }
              />
            </Field>
            <div className={s.note}>
              {t(
                "Paste straight from the contractor's spreadsheet — comma, semicolon or tab separated; a header line is skipped.",
              )}{" "}
              {parsed.rows.length > 0 && <b>{t("{n} results read.", { n: parsed.rows.length })}</b>}{" "}
              {parsed.errors.length > 0 && (
                <span className={s.fail}>
                  {parsed.errors.slice(0, 3).join(" · ")}
                  {parsed.errors.length > 3 ? ` · ${t("and {n} more", { n: parsed.errors.length - 3 })}` : ""}
                </span>
              )}
            </div>
            {type.thresholds.length > 0 && (
              <div className={s.note}>
                {t("Judged against:")}{" "}
                {type.thresholds
                  .map((th) =>
                    th.basis === "pct_of_specified"
                      ? t("{label} — attention {attention}, action {fail} % of specified", {
                          label: th.label,
                          attention: th.attention ?? "—",
                          fail: th.fail ?? "—",
                        })
                      : t("{label} — attention {attention}, action {fail}", {
                          label: th.label,
                          attention: th.attention ?? "—",
                          fail: th.fail ?? "—",
                        }),
                  )
                  .join("; ")}
                {type.thresholds.some((th) => th.status !== "agreed") && (
                  <span className={`${s.badge} ${s.proposed}`} style={{ marginInlineStart: 6 }}>
                    {t("PROPOSED")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {type?.mode === "ratio" && type.ratio && (
          <div className={`${s.wide} ${s.note}`}>
            {t("Passes when {value} is at or above {limit}.", {
              value: type.ratio.valueLabel ?? type.ratio.value,
              limit: type.ratio.limitLabel ?? type.ratio.limit,
            })}
            {type.ratio.status !== "agreed" && (
              <span className={`${s.badge} ${s.proposed}`} style={{ marginInlineStart: 6 }}>
                {t("PROPOSED")}
              </span>
            )}
          </div>
        )}

        {type?.mode === "judged" && (
          <>
            <Field label="Verdict">
              <Select value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
                <option value="pass">{t("Pass")}</option>
                <option value="attention">{t("Needs attention")}</option>
                <option value="fail">{t("Action needed")}</option>
              </Select>
            </Field>
            <div className={s.note} style={{ alignSelf: "end" }}>
              {t("This test is judged by the engineer; the verdict needs its basis.")}
            </div>
            <div className={s.wide}>
              <Field label="Basis for the verdict (required)">
                <textarea
                  className={s.textarea}
                  rows={3}
                  value={basis}
                  onChange={(e) => setBasis(e.target.value)}
                  placeholder={t("What was found, against which levels, and what follows")}
                />
              </Field>
            </div>
          </>
        )}

        <Field label="Next test due (optional)">
          <Input type="date" value={f.nextDue} onChange={(e) => set("nextDue")(e.target.value)} />
        </Field>
        <div className={s.note} style={{ alignSelf: "end" }}>
          {type?.intervalDays
            ? type.intervalStatus !== "agreed"
              ? t("Blank uses the interval: every {n} days (proposed).", { n: type.intervalDays })
              : t("Blank uses the interval: every {n} days.", { n: type.intervalDays })
            : t("This test has no interval set.")}
        </div>
        <div className={s.wide}>
          <Field label="Notes">
            <textarea className={s.textarea} rows={2} value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
          </Field>
        </div>
        <div className={s.wide}>
          <Field label="Test report and data files (PDF, image, CSV or Excel, up to 25 MB each)">
            <input
              type="file"
              multiple
              accept=".pdf,.csv,.xlsx,image/*"
              onChange={(e) => setFiles([...(e.target.files ?? [])])}
            />
          </Field>
        </div>

        <div className={s.foot}>
          {saving.isError && (
            <span className={s.fail} role="alert" style={{ marginInlineEnd: "auto" }}>
              {t("Not recorded: {message}", { message: (saving.error as Error).message })}
            </span>
          )}
          {uploadError && (
            <span className={s.fail} role="alert" style={{ marginInlineEnd: "auto" }}>
              {t("Recorded, but a file did not upload — {error}. Add it from the record.", { error: uploadError })}
            </span>
          )}
          <Button onClick={onClose} disabled={saving.isPending}>
            {createdId ? t("Close") : t("Cancel")}
          </Button>
          {uploadError && createdId ? (
            <Button variant="primary" onClick={() => onCreated(createdId)}>
              {t("Open the record")}
            </Button>
          ) : (
            <Button type="submit" variant="primary" disabled={!ready || saving.isPending || !!createdId}>
              {saving.isPending
                ? t("Saving…")
                : createdId
                  ? t("Uploading files…")
                  : revise
                    ? t("Save revision {n}", { n: rec!.revision + 1 })
                    : t("Record and judge")}
            </Button>
          )}
        </div>
      </form>
    </dialog>
  );
}
